"use client";

import { User, Building } from "lucide-react";
import { Label } from "@/components/ui/label";
import { BookingCard } from "./BookingCard";
import { LanguageSelector } from "./LanguageSelector";
import { useTranslation } from "react-i18next";
import Link from "next/link";

type BookingType = "private" | "company" | null;

interface BookingSectionProps {
  onContinue: (type: BookingType) => void;
}

export function BookingSection({ onContinue }: BookingSectionProps) {
  const { t } = useTranslation("common");

  return (
    <section className="w-full min-h-screen flex flex-col relative">
      {/* Language selector - positioned absolutely in top right */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageSelector availableLanguages={["en", "sv", "no", "da"]} />
      </div>

      <div className="flex-1 flex items-center justify-center py-8 px-4 md:py-12">
        <div className="w-full max-w-3xl mx-auto">
          {/* Invisible spacer to match back button height on other steps */}
          <div className="h-4 mb-6"></div>

          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img
              src="https://onair-prod-assets.s3.eu-west-1.amazonaws.com/app-assets/on-air-logo.png"
              alt="On Air Game Shows"
              className="h-16 md:h-20 w-auto p-3 rounded-lg shadow-md"
              style={{
                backgroundImage:
                  "url(https://onair-prod-assets.s3.eu-west-1.amazonaws.com/app-assets/footer-background-sm.jpg)",
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
          </div>

          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-3">
            {t("booking.title")}
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground text-center mb-8 md:mb-12 px-4">
            {t("booking.subtitle")}
          </p>

          <div className="space-y-6 md:space-y-8">
            <div className="space-y-6">
              <Label className="text-base sm:text-lg block text-center">
                {t("booking.typeLabel")}
              </Label>

              <div className="flex flex-col sm:flex-row justify-center items-center sm:items-stretch gap-3 sm:gap-6 px-0 sm:px-4">
                <div className="w-full max-w-[280px] sm:w-[250px] flex">
                  <BookingCard
                    icon={User}
                    title={t("booking.types.private.title")}
                    description={t("booking.types.private.description")}
                    onClick={() => onContinue("private")}
                  />
                </div>

                <div className="w-full max-w-[280px] sm:w-[250px] flex">
                  <BookingCard
                    icon={Building}
                    title={t("booking.types.company.title")}
                    description={t("booking.types.company.description")}
                    onClick={() => onContinue("company")}
                  />
                </div>
              </div>
            </div>

            {/* Already have a booking link */}
            <div className="text-center mt-8">
              <p className="text-sm text-muted-foreground">
                {t("booking.alreadyHaveBooking")}{" "}
                <Link
                  href="/login-otp"
                  className="text-primary underline hover:text-primary/80 transition-colors"
                >
                  {t("booking.loginHere")}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      <footer className="py-6 text-center text-sm text-muted-foreground">
        {t("booking.footer")}
      </footer>
    </section>
  );
}
